import { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Search, X, ChevronRight, MessageCircle, Filter, MapPin, Phone, Clock, ExternalLink, Plus, Minus, Trash2, Copy, Check, RotateCw, Heart, Eye, Tag, ChevronDown, Download } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { PhoneInput } from '../../components/PhoneInput';
import { getTheme } from '../../lib/themes';
import { toast } from 'sonner';
import { CatalogoOfflineModal } from '../../components/public/CatalogoOfflineModal';
import { shouldPromptOffline, getOfflineInfo, downloadCatalog } from '../../lib/offlineCatalog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WHATSAPP_NUMBER_FALLBACK = '5215512345678';

// Carrito persistente en localStorage (compartido con ProductoDetalle)
const CART_KEY = 'kj_cart_v1';
const loadCartLS = () => {
    try { const r = localStorage.getItem(CART_KEY); return r ? JSON.parse(r) : []; } catch { return []; }
};
const saveCartLS = (cart) => {
    try { localStorage.setItem(CART_KEY, JSON.stringify(cart)); } catch { /* ignore */ }
};

// ============== STATE MEMORY (Scroll, filtros, categoria) ==============
// Persistimos en sessionStorage para que al regresar de un producto el cliente
// vea exactamente donde se quedo. Se limpia al cerrar la pestaña (sessionStorage).
const STATE_KEY = 'kj_tienda_state_v1';
const loadState = () => {
    try { const r = sessionStorage.getItem(STATE_KEY); return r ? JSON.parse(r) : null; } catch { return null; }
};
const saveState = (state) => {
    try { sessionStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
};

// Favoritos persistentes (localStorage para que sobrevivan cierre de pestaña)
const FAVS_KEY = 'kj_favoritos_v1';
const loadFavs = () => {
    try { const r = localStorage.getItem(FAVS_KEY); return r ? JSON.parse(r) : []; } catch { return []; }
};
const saveFavs = (favs) => {
    try { localStorage.setItem(FAVS_KEY, JSON.stringify(favs)); } catch { /* ignore */ }
};

// Vistos recientemente
const RECIENT_KEY = 'kj_recientes_v1';
const loadRecientes = () => {
    try { const r = localStorage.getItem(RECIENT_KEY); return r ? JSON.parse(r) : []; } catch { return []; }
};
const saveRecientes = (arr) => {
    try { localStorage.setItem(RECIENT_KEY, JSON.stringify(arr)); } catch { /* ignore */ }
};

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] } })
};

export const Tienda = () => {
    // Cargar estado guardado de sesion anterior (si volvio desde un producto)
    const savedState = loadState();

    // Deshabilitar el scroll restoration nativo del browser desde el primer render
    // para evitar conflictos con nuestro restore manual.
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
        try { window.history.scrollRestoration = 'manual'; } catch (e) { /* ignore */ }
    }

    const [productos, setProductos] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState(savedState?.searchTerm || '');
    const [selectedColor, setSelectedColor] = useState(savedState?.selectedColor || '');
    const [selectedTalla, setSelectedTalla] = useState(savedState?.selectedTalla || '');
    const [selectedCategoria, setSelectedCategoria] = useState(savedState?.selectedCategoria || '');
    const [selectedMarca, setSelectedMarca] = useState(savedState?.selectedMarca || '');
    const [priceRange, setPriceRange] = useState(savedState?.priceRange || [0, 5000]);
    const [showFilters, setShowFilters] = useState(false);
    const [cart, setCart] = useState(loadCartLS());
    const [cartOpen, setCartOpen] = useState(false);
    const [config, setConfig] = useState(null);
    const [searchParams] = useSearchParams();
    // Categoria activa (cholo, vaquero, nino, playeras, estampado)
    const [activeCat, setActiveCat] = useState(savedState?.activeCat || 'cholo');
    const [playeras, setPlayeras] = useState([]);
    // Info de marcas (banner/logo/descripcion configurada en admin)
    const [marcasAdmin, setMarcasAdmin] = useState([]);

    // Cargar info enriquecida de marcas
    useEffect(() => {
        axios.get(`${API_URL}/api/tienda/marcas`).then(r => setMarcasAdmin(r.data || [])).catch(() => setMarcasAdmin([]));
    }, []);

    // Favoritos y Vistos Recientemente
    const [favoritos, setFavoritos] = useState(loadFavs());
    const [recientes, setRecientes] = useState(loadRecientes());
    const [showFavoritos, setShowFavoritos] = useState(false);
    const [showMarcasMenu, setShowMarcasMenu] = useState(false);
    const [showOffline, setShowOffline] = useState(false);

    // Infinite scroll: cantidad de productos a mostrar
    const [pageSize, setPageSize] = useState(savedState?.pageSize || 24);

    // Refs para guardar scroll y restaurar
    const scrollRestored = useRef(false);
    const restoreScrollY = savedState?.scrollY || 0;

    // Toggle favorito
    const toggleFavorito = (productId) => {
        setFavoritos(prev => {
            const next = prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId];
            saveFavs(next);
            toast.success(prev.includes(productId) ? 'Quitado de favoritos' : '❤️ Agregado a favoritos');
            return next;
        });
    };

    // Persistir SOLO filtros (no scrollY) en sessionStorage. scrollY se persiste aparte.
    useEffect(() => {
        const prev = loadState() || {};
        saveState({
            ...prev,
            searchTerm, selectedColor, selectedTalla, selectedCategoria, selectedMarca,
            priceRange, activeCat, pageSize,
        });
    }, [searchTerm, selectedColor, selectedTalla, selectedCategoria, selectedMarca, priceRange, activeCat, pageSize]);

    // Guardar scrollY antes de salir a un producto. Capturamos en multiples puntos
    // para garantizar que NO se pierda: click en Link, beforeunload, pagehide.
    useEffect(() => {
        const persistScroll = () => {
            const prev = loadState() || {};
            prev.scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
            saveState(prev);
        };
        const onClick = (e) => {
            const a = e.target.closest('a[href^="/producto/"], a[href^="/pedido/"]');
            if (a) persistScroll();
        };
        document.addEventListener('click', onClick, true);
        window.addEventListener('beforeunload', persistScroll);
        window.addEventListener('pagehide', persistScroll);
        return () => {
            document.removeEventListener('click', onClick, true);
            window.removeEventListener('beforeunload', persistScroll);
            window.removeEventListener('pagehide', persistScroll);
        };
    }, []);

    // Restaurar scroll cuando los productos carguen. Usar useLayoutEffect para hacerlo
    // ANTES del paint, y varios rAF + timeouts para esperar a que las imagenes carguen
    // y empujen el layout.
    useLayoutEffect(() => {
        if (scrollRestored.current) return;
        if (loading) return;
        if (productos.length === 0) return;
        if (restoreScrollY <= 0) { scrollRestored.current = true; return; }

        // Anular el scroll restoration automatico del browser para evitar conflictos
        try { if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual'; } catch (e) { /* ignore */ }

        let attempts = 0;
        const maxAttempts = 20; // ~2 segundos esperando layout estable
        const tryRestore = () => {
            attempts++;
            window.scrollTo({ top: restoreScrollY, behavior: 'instant' });
            // Verificar si llegamos cerca (las imagenes pueden empujar layout)
            const reached = Math.abs(window.scrollY - restoreScrollY) < 50;
            if (reached || attempts >= maxAttempts) {
                scrollRestored.current = true;
                return;
            }
            setTimeout(tryRestore, 100);
        };
        // Comenzar despues de 2 frames para que React termine de renderizar
        requestAnimationFrame(() => requestAnimationFrame(tryRestore));
    }, [loading, productos.length, restoreScrollY]);

    // Cargar playeras publicas (cuando se necesite)
    useEffect(() => {
        if (activeCat === 'playeras') {
            axios.get(`${API_URL}/api/tienda/playeras`)
                .then(r => setPlayeras(r.data || []))
                .catch(() => setPlayeras([]));
        }
    }, [activeCat]);

    const t = useMemo(() => {
        if (!config) return getTheme('denim_premium');
        return getTheme(config.tema_activo);
    }, [config]);

    const heroTagline = config?.hero_tagline || 'Fabricantes Directos';
    const heroTitle1 = config?.hero_title_1 || 'KARRY';
    const heroTitle2 = config?.hero_title_2 || "JEAN'S";
    const heroSubtitle = config?.hero_subtitle || 'Los mejores precios de Mexico. Vaqueros, cholos y ropa para nino directo de fabrica.';
    const heroCta = config?.hero_cta || 'Ver catalogo';
    const bannerText = config?.banner_text || '';
    const useAnimations = config?.animaciones !== false;
    const layoutCols = config?.layout_catalogo || 'grid-4';

    const categoriasConfig = config?.categorias_config || [];
    const activeCatConfig = categoriasConfig.find(c => c.slug === activeCat) || {};

    // Hero dinamico: si hay config de categoria, usar; si no, fallback
    const dynamicHeroImage = activeCatConfig.image || config?.hero_image || '';
    const dynamicHeroTitle = activeCatConfig.title || `${heroTitle1} ${heroTitle2}`;
    const dynamicHeroTagline = activeCatConfig.tagline || heroSubtitle;

    const heroImage = dynamicHeroImage;

    const gridClass = useMemo(() => {
        switch (layoutCols) {
            case 'grid-2': return 'grid-cols-1 sm:grid-cols-2';
            case 'grid-3': return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
            default: return 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
        }
    }, [layoutCols]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [prodRes, configRes] = await Promise.all([
                    axios.get(`${API_URL}/api/tienda/productos`),
                    axios.get(`${API_URL}/api/tienda/config`)
                ]);
                setProductos(prodRes.data);
                setFiltered(prodRes.data);
                setConfig(configRes.data);
            } catch (error) {
                console.error('Error:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (loading || productos.length === 0) return;
        if (shouldPromptOffline()) {
            const tm = setTimeout(() => setShowOffline(true), 1200);
            return () => clearTimeout(tm);
        }
        const info = getOfflineInfo();
        if (info && navigator.onLine && Date.now() - info.at > 12 * 3600 * 1000) downloadCatalog().catch(() => {});
    }, [loading, productos.length]);

    useEffect(() => {
        let result = productos;
        // Filtrado dinamico por categoria activa (tab). Funciona para CUALQUIER slug
        // (cholo, vaquero, nino, bermuda, sudadera, etc.) sin necesidad de hardcodear.
        // Las playeras tienen su propia vista separada (no aplica aqui).
        if (activeCat && activeCat !== 'playeras') {
            const slug = activeCat.toLowerCase();
            // Aliases conocidos para mayor flexibilidad de match
            const aliases = {
                vaquero: ['vaquero', 'jean', 'mezclilla'],
                nino: ['nino', 'niño'],
            };
            const keywords = aliases[slug] || [slug];
            result = result.filter(p => {
                const cat = (p.categoria || '').toLowerCase();
                const seg = (p.segmento || '').toLowerCase();
                const nom = (p.nombre || '').toLowerCase();
                return keywords.some(k => cat.includes(k) || seg.includes(k) || nom.includes(k));
            });
        }
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(p =>
                p.nombre?.toLowerCase().includes(term) ||
                p.descripcion?.toLowerCase().includes(term) ||
                p.marca?.toLowerCase().includes(term) ||
                p.sku?.toLowerCase().includes(term) ||
                p.color?.toLowerCase().includes(term) ||
                p.categoria?.toLowerCase().includes(term)
            );
        }
        if (selectedColor) result = result.filter(p => p.color === selectedColor);
        if (selectedTalla) result = result.filter(p => p.tallas_disponibles?.includes(selectedTalla));
        if (selectedCategoria) result = result.filter(p => p.categoria === selectedCategoria);
        if (selectedMarca) result = result.filter(p => (p.marca || '').toLowerCase() === selectedMarca.toLowerCase());
        result = result.filter(p => (p.precio || 0) >= priceRange[0] && (p.precio || 0) <= priceRange[1]);
        setFiltered(result);
    }, [searchTerm, selectedColor, selectedTalla, selectedCategoria, selectedMarca, priceRange, productos, activeCat]);

    useEffect(() => { saveCartLS(cart); }, [cart]);
    useEffect(() => {
        if (searchParams.get('cart') === '1') {
            setCartOpen(true);
            setCart(loadCartLS());
        }
    }, [searchParams]);

    const allColors = [...new Set(productos.map(p => p.color).filter(Boolean))];
    const allTallas = [...new Set(productos.flatMap(p => p.tallas_disponibles || []))];
    const allCategorias = [...new Set(productos.map(p => p.categoria).filter(Boolean))];
    // Marcas detectadas en productos (con contador por marca)
    const marcasInfo = useMemo(() => {
        const map = {};
        for (const p of productos) {
            const m = (p.marca || '').trim();
            if (!m) continue;
            map[m] = (map[m] || 0) + 1;
        }
        return Object.entries(map).map(([nombre, count]) => ({ nombre, count })).sort((a, b) => b.count - a.count);
    }, [productos]);

    const updateCantidad = (idx, delta) => {
        setCart(cart.map((item, i) => i === idx ? { ...item, cantidad: Math.max(1, item.cantidad + delta) } : i === idx && item.cantidad + delta <= 0 ? null : item).filter(Boolean));
    };

    const addToCart = (producto, talla, tipoCompra = 'pieza') => {
        // Soporta pieza/paquete/corrida (usado por ProductoDetalle y PlayeraCard)
        const tc = tipoCompra || 'pieza';
        const cantidad = tc === 'paquete' ? (producto.tamano_paquete || 14)
            : tc === 'corrida' ? Math.ceil((producto.tamano_paquete || 14) / 2)
            : 1;
        const existing = cart.find(c => c.producto_id === producto.id && c.talla === talla && (c.tipo_compra || 'pieza') === tc);
        if (existing) {
            setCart(cart.map(c => c === existing ? { ...c, cantidad: c.cantidad + cantidad } : c));
        } else {
            setCart([...cart, {
                id: `${producto.id}-${tc}-${talla}-${Date.now()}`,
                producto_id: producto.id,
                nombre: producto.nombre,
                imagen: producto.imagenes?.[0] || '',
                talla,
                color: producto.color || '',
                cantidad,
                precio: producto.precio,
                tipo_compra: tc,
                es_playera: !!producto.es_playera,
            }]);
        }
        setCartOpen(true);
    };

    const removeFromCart = (index) => setCart(cart.filter((_, i) => i !== index));

    const cartTotal = cart.reduce((sum, item) => sum + ((item.precio || 0) * item.cantidad), 0);

    const waNumber = (config?.whatsapp_numero || WHATSAPP_NUMBER_FALLBACK).replace(/\D/g, '');
    const waBaseMsg = config?.whatsapp_mensaje || 'Hola! Me interesa hacer un pedido';

    // Modal de checkout: capturar datos antes de enviar a WhatsApp
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [checkoutData, setCheckoutData] = useState({
        nombre: '', telefono: '', metodo_envio: 'tienda', ciudad: '', estado: '', direccion: '', notas: ''
    });
    const [lastPedido, setLastPedido] = useState(null);

    const openCheckout = () => {
        if (cart.length === 0) { toast.error('Agrega productos primero'); return; }
        setCheckoutOpen(true);
    };

    const armarMensajeWA = (pedido) => {
        const items = cart.map(item => {
            const tc = item.tipo_compra || 'pieza';
            const tcLabel = tc === 'paquete' ? 'Paquete' : tc === 'corrida' ? 'Corrida' : 'Pieza';
            return `• ${item.nombre} (T:${item.talla}) - ${tcLabel} x${item.cantidad} = $${(item.precio * item.cantidad).toFixed(2)}`;
        }).join('\n');

        const envioLabel = {
            paqueteria: 'Paqueteria a domicilio',
            camion: 'Camion Texticuitzeo',
            tienda: 'Paso a tienda'
        }[pedido.metodo_envio] || pedido.metodo_envio;

        const trackingUrl = `${window.location.origin}/pedido/${pedido.codigo_seguimiento}`;
        const mapsLine = (pedido.metodo_envio === 'tienda' && config?.tienda_direccion)
            ? `\n📍 Direccion tienda: ${config.tienda_direccion}${config.google_maps_link ? '\n🗺️ Mapa: ' + config.google_maps_link : ''}`
            : '';

        return `${waBaseMsg}:\n\n📦 PRODUCTOS:\n${items}\n\n💰 Total aproximado: $${pedido.total.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN\n\n👤 Nombre: ${pedido.cliente_nombre}\n📞 Telefono: ${pedido.cliente_telefono}\n🚚 Envio: ${envioLabel}${pedido.ciudad ? '\n🏙️ Ciudad: ' + pedido.ciudad : ''}${pedido.estado_envio ? ', ' + pedido.estado_envio : ''}${pedido.direccion_envio ? '\n📮 Direccion: ' + pedido.direccion_envio : ''}${pedido.notas ? '\n📝 Notas: ' + pedido.notas : ''}${mapsLine}\n\n🔑 Codigo de seguimiento: ${pedido.codigo_seguimiento}\n🔎 Sigue tu pedido aqui: ${trackingUrl}`;
    };

    const submitCheckout = async () => {
        if (!checkoutData.nombre.trim()) { toast.error('Necesitamos tu nombre'); return; }
        if (!checkoutData.telefono.trim()) { toast.error('Necesitamos tu telefono'); return; }
        setSubmitting(true);
        try {
            const items = cart.map(item => ({
                producto_id: item.id,
                nombre: item.nombre,
                talla: item.talla,
                color: item.color || '',
                cantidad: item.cantidad,
                precio: item.precio,
                tipo_compra: item.tipo_compra || 'pieza',
                subtotal: (item.precio || 0) * item.cantidad
            }));
            const tipos = [...new Set(cart.map(c => c.tipo_compra || 'pieza'))];
            const tipo_compra = tipos.length === 1 ? tipos[0] : 'mixto';

            const payload = {
                cliente_nombre: checkoutData.nombre.trim(),
                cliente_telefono: checkoutData.telefono.trim(),
                items,
                total: cartTotal,
                notas: checkoutData.notas || '',
                tipo_compra,
                metodo_envio: checkoutData.metodo_envio,
                ciudad: checkoutData.ciudad || '',
                estado_envio: checkoutData.estado || '',
                direccion_envio: checkoutData.direccion || ''
            };
            const res = await axios.post(`${API_URL}/api/tienda/pedido`, payload);
            const pedido = res.data;
            setLastPedido(pedido);

            // Abrir WhatsApp con mensaje formateado
            const mensaje = encodeURIComponent(armarMensajeWA(pedido));
            window.open(`https://wa.me/${waNumber}?text=${mensaje}`, '_blank');

            toast.success(`Pedido enviado! Codigo: ${pedido.codigo_seguimiento}`);
            setCart([]);
            setCheckoutOpen(false);
            setCartOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al enviar el pedido');
        } finally {
            setSubmitting(false);
        }
    };

    const sendQuickMessage = (tipo) => {
        const mensajes = {
            cotizar: 'Hola, vi algunos productos en su pagina y me gustaria solicitar una cotizacion para compra mayoreo.',
            contactar: 'Hola, vi su pagina web y me gustaria recibir mas informacion sobre sus productos, precios y ventas por mayoreo. Quedo atento(a), gracias.',
            mayoreo: 'Hola, me interesa la venta por paquete/corrida al mayoreo. Podria pasarme precios e informacion?'
        };
        const msg = encodeURIComponent(mensajes[tipo] || mensajes.contactar);
        window.open(`https://wa.me/${waNumber}?text=${msg}`, '_blank');
    };

    const checkoutWhatsApp = openCheckout;

    const clearFilters = () => {
        setSearchTerm(''); setSelectedColor(''); setSelectedTalla(''); setSelectedCategoria(''); setPriceRange([0, 5000]);
    };

    const Wrap = useAnimations ? motion.div : 'div';
    const wrapProps = useAnimations ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.5 } } : {};

    return (
        <Wrap {...wrapProps} className="min-h-screen" style={{ background: t.colors.bg, color: t.colors.text, fontFamily: t.font }} data-testid="tienda-page">
            {/* Google Fonts */}
            <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Bebas+Neue&family=Poppins:wght@300;400;500;600;700&family=DM+Sans:wght@400;500;700&family=Libre+Baskerville:wght@400;700&family=Inter:wght@400;500;600;700&family=Space+Grotesk:wght@400;500;600;700&family=Cormorant+Garamond:wght@400;600;700&display=swap" />

            {/* Banner */}
            {bannerText && (
                <div className="fixed top-0 w-full z-[60] py-1.5 text-center text-[10px] sm:text-xs tracking-[0.15em] font-bold"
                    style={{ background: t.colors.accent, color: t.colors.badgeText }} data-testid="promo-banner">
                    {bannerText}
                </div>
            )}

            {/* Navbar */}
            <header className="fixed w-full z-50 backdrop-blur-xl border-b"
                style={{ top: bannerText ? '28px' : '0', background: t.colors.navBg, borderColor: t.colors.border }}>
                <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
                    <Link to="/tienda" className="flex items-center gap-2">
                        <img src="/logo-karry.png" alt="Karry Jean's" className="w-8 h-8 rounded-md object-contain" />
                        <span className="text-lg sm:text-xl tracking-wider font-bold" style={{ fontFamily: t.font }}>KARRY JEAN'S</span>
                    </Link>
                    <div className="flex items-center gap-2 sm:gap-4">
                        <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-xs sm:text-sm transition-colors hover:opacity-80"
                            style={{ color: t.colors.textMuted }} data-testid="whatsapp-link">
                            <MessageCircle className="w-4 h-4" /> <span className="hidden sm:inline">WhatsApp</span>
                        </a>
                        <a href="/login" className="flex items-center gap-1.5 text-xs sm:text-sm transition-colors hover:opacity-80"
                            style={{ color: t.colors.textMuted }} data-testid="admin-link">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                            <span className="hidden sm:inline">Admin</span>
                        </a>
                        <button onClick={() => setCartOpen(!cartOpen)} className="relative p-2 rounded-lg transition-colors hover:opacity-80" data-testid="cart-toggle">
                            <ShoppingBag className="w-5 h-5" />
                            {cart.length > 0 && (
                                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold"
                                    style={{ background: t.colors.badge, color: t.colors.badgeText }}>
                                    {cart.length}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative flex items-center overflow-hidden"
                style={{ paddingTop: bannerText ? 'calc(4rem + 28px)' : '4rem', minHeight: '70vh' }}>
                {heroImage && (
                    <div className="absolute inset-0">
                        <img src={`${API_URL}${heroImage}`} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0" style={{ background: `${t.colors.bg}cc` }} />
                    </div>
                )}
                <div className="absolute inset-0" style={{ background: t.hero.gradient || 'none' }} />
                <div className="relative max-w-7xl mx-auto px-4 text-center w-full py-20">
                    {useAnimations ? (
                        <>
                            <motion.p key={`tag-${activeCat}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                                className="tracking-[0.3em] text-xs uppercase mb-4 font-bold" style={{ color: t.colors.accent }}>
                                ★ {heroTagline} ★
                            </motion.p>
                            <motion.h1 key={`h1-${activeCat}`} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}
                                className="text-4xl sm:text-6xl lg:text-7xl tracking-wider mb-6 font-bold leading-tight" style={{ fontFamily: t.font }}>
                                {dynamicHeroTitle}
                            </motion.h1>
                            <motion.p key={`sub-${activeCat}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
                                className="text-base sm:text-lg max-w-xl mx-auto mb-8" style={{ color: t.colors.textMuted }}>
                                {dynamicHeroTagline}
                            </motion.p>
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }}
                                className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                                <a href="#catalogo" className="inline-flex items-center gap-2 px-8 py-3.5 font-bold transition-transform hover:scale-105 shadow-xl"
                                    style={{ background: t.colors.accent, color: t.colors.badgeText, borderRadius: t.buttonRadius }}
                                    data-testid="ver-catalogo-btn">
                                    {heroCta} <ChevronRight className="w-4 h-4" />
                                </a>
                                <button onClick={() => sendQuickMessage('mayoreo')}
                                    className="inline-flex items-center gap-2 px-8 py-3.5 font-bold transition-transform hover:scale-105 shadow-xl"
                                    style={{ background: '#25D366', color: '#fff', borderRadius: t.buttonRadius }}
                                    data-testid="hero-wa-btn">
                                    <MessageCircle className="w-4 h-4" /> Hacer pedido por WhatsApp
                                </button>
                            </motion.div>
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
                                className="mt-10 flex flex-wrap justify-center gap-6 text-xs uppercase tracking-widest" style={{ color: t.colors.textMuted }}>
                                <span>★ Envios a todo Mexico</span>
                                <span>★ Mayoreo y menudeo</span>
                                <span>★ Calidad garantizada</span>
                            </motion.div>
                        </>
                    ) : (
                        <>
                            <p className="tracking-[0.3em] text-xs uppercase mb-4 font-bold" style={{ color: t.colors.accent }}>★ {heroTagline} ★</p>
                            <h1 className="text-4xl sm:text-6xl lg:text-7xl tracking-wider mb-6 font-bold leading-tight" style={{ fontFamily: t.font }}>
                                {dynamicHeroTitle}
                            </h1>
                            <p className="text-base sm:text-lg max-w-xl mx-auto mb-8" style={{ color: t.colors.textMuted }}>{dynamicHeroTagline}</p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                                <a href="#catalogo" className="inline-flex items-center gap-2 px-8 py-3.5 font-bold shadow-xl"
                                    style={{ background: t.colors.accent, color: t.colors.badgeText, borderRadius: t.buttonRadius }}
                                    data-testid="ver-catalogo-btn">
                                    {heroCta} <ChevronRight className="w-4 h-4" />
                                </a>
                                <button onClick={() => sendQuickMessage('mayoreo')}
                                    className="inline-flex items-center gap-2 px-8 py-3.5 font-bold shadow-xl"
                                    style={{ background: '#25D366', color: '#fff', borderRadius: t.buttonRadius }}>
                                    <MessageCircle className="w-4 h-4" /> Hacer pedido por WhatsApp
                                </button>
                            </div>
                            <div className="mt-10 flex flex-wrap justify-center gap-6 text-xs uppercase tracking-widest" style={{ color: t.colors.textMuted }}>
                                <span>★ Envios a todo Mexico</span>
                                <span>★ Mayoreo y menudeo</span>
                                <span>★ Calidad garantizada</span>
                            </div>
                        </>
                    )}
                </div>
            </section>

            {/* Tabs de categorias */}
            <section className="sticky top-14 z-20 backdrop-blur-xl border-b" style={{ background: `${t.colors.bg}f0`, borderColor: t.colors.border }}>
                <div className="max-w-7xl mx-auto px-3 sm:px-4">
                    {/* MOBILE: Grid 3 columnas (todas las categorias visibles sin scroll) */}
                    {/* DESKTOP: Scroll horizontal tradicional */}
                    <div
                        className="grid grid-cols-3 gap-1.5 py-2.5 sm:flex sm:gap-2 sm:overflow-x-auto sm:scrollbar-hide sm:py-3"
                        data-testid="category-tabs"
                    >
                        {(categoriasConfig.length > 0 ? categoriasConfig : [
                            { slug: 'cholo', label: 'Cholos' },
                            { slug: 'vaquero', label: 'Vaqueros' },
                            { slug: 'nino', label: 'Niño' },
                            { slug: 'playeras', label: 'Playeras' }
                        ]).map((c) => {
                            const isActive = activeCat === c.slug;
                            return (
                                <button
                                    key={c.slug}
                                    onClick={(e) => {
                                        setActiveCat(c.slug);
                                        const el = document.getElementById('catalogo');
                                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                        try { e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' }); } catch (err) { /* ignore */ }
                                    }}
                                    className="
                                        w-full sm:w-auto shrink-0
                                        px-2 sm:px-5
                                        py-2 sm:py-2
                                        rounded-lg sm:rounded-full
                                        text-[11px] sm:text-sm
                                        font-bold uppercase
                                        tracking-tight sm:tracking-wider
                                        transition-all
                                        whitespace-nowrap
                                        truncate
                                    "
                                    style={{
                                        background: isActive ? t.colors.accent : `${t.colors.cardBg || t.colors.bg}80`,
                                        color: isActive ? t.colors.badgeText : t.colors.textMuted,
                                        border: `1.5px solid ${isActive ? t.colors.accent : t.colors.border}`,
                                        transform: isActive ? 'scale(1.03)' : 'scale(1)',
                                        boxShadow: isActive ? `0 4px 12px -2px ${t.colors.accent}40` : 'none',
                                    }}
                                    data-testid={`cat-tab-${c.slug}`}
                                    title={c.label || c.slug}
                                >
                                    {c.label || c.slug}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Catalog */}
            <section id="catalogo" className="max-w-7xl mx-auto px-4 py-16">
                {/* Filters Bar */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
                    <div>
                        <h2 className="text-3xl tracking-wider font-bold" style={{ fontFamily: t.font }}>CATALOGO</h2>
                        <p className="text-sm mt-1" style={{ color: t.colors.textMuted }}>
                            {activeCat === 'playeras' ? `${playeras.length} playera${playeras.length !== 1 ? 's' : ''}` : `${filtered.length} producto${filtered.length !== 1 ? 's' : ''}`}
                        </p>
                    </div>
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        <div className="relative flex-1 sm:w-72">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 z-10" style={{ color: t.colors.textMuted }} />
                            <Input
                                placeholder="Buscar marca, modelo, SKU..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                                style={{ background: t.colors.surface, borderColor: t.colors.border, color: t.colors.text }}
                                data-testid="search-productos"
                            />
                            {/* Dropdown de resultados instantaneos (estilo Mercado Libre) */}
                            {searchTerm.trim().length >= 2 && filtered.length > 0 && filtered.length < productos.length && (
                                <div
                                    className="absolute top-full left-0 right-0 mt-1 max-h-[400px] overflow-y-auto rounded-lg shadow-2xl z-50"
                                    style={{ background: t.colors.bg, border: `1px solid ${t.colors.border}` }}
                                    data-testid="search-dropdown"
                                >
                                    <div className="px-3 py-2 border-b text-[10px] uppercase tracking-wider font-bold sticky top-0 backdrop-blur-xl" style={{ background: `${t.colors.bg}f0`, borderColor: t.colors.border, color: t.colors.textMuted }}>
                                        {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
                                    </div>
                                    {filtered.slice(0, 8).map(p => (
                                        <Link
                                            key={p.id}
                                            to={`/producto/${p.id}`}
                                            onClick={() => setSearchTerm('')}
                                            className="flex items-center gap-3 p-2 hover:opacity-80 transition border-b last:border-b-0"
                                            style={{ borderColor: t.colors.border }}
                                            data-testid={`search-result-${p.id}`}
                                        >
                                            <div className="w-12 h-14 shrink-0 rounded overflow-hidden" style={{ background: t.colors.surface }}>
                                                {p.imagenes?.[0]
                                                    ? <img src={p.imagenes[0]} alt={p.nombre} loading="lazy" className="w-full h-full object-cover" />
                                                    : <div className="w-full h-full flex items-center justify-center"><ShoppingBag className="w-5 h-5" style={{ color: t.colors.border }} /></div>}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[10px] uppercase tracking-wider truncate" style={{ color: t.colors.textMuted }}>{p.marca || 'Sin marca'}</p>
                                                <p className="text-xs font-medium truncate">{p.nombre}</p>
                                                <p className="text-xs font-bold" style={{ color: t.colors.accent }}>${p.precio?.toLocaleString('es-MX')}</p>
                                            </div>
                                        </Link>
                                    ))}
                                    {filtered.length > 8 && (
                                        <div className="px-3 py-2 text-[10px] text-center" style={{ color: t.colors.textMuted }}>
                                            +{filtered.length - 8} resultados mas en la lista
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <button onClick={() => setShowFilters(!showFilters)}
                            className="p-2.5 rounded-lg transition-colors"
                            style={{ background: t.colors.surface, border: `1px solid ${t.colors.border}` }}
                            data-testid="toggle-filters">
                            <Filter className="w-4 h-4" style={{ color: t.colors.textMuted }} />
                        </button>
                    </div>
                </div>

                {/* Filter Panel */}
                <AnimatePresence>
                    {showFilters && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden mb-8">
                            <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4"
                                style={{ background: t.colors.surface, border: `1px solid ${t.colors.border}`, borderRadius: t.cardRadius }}>
                                <div>
                                    <p className="text-xs uppercase tracking-wider mb-2" style={{ color: t.colors.textMuted }}>Color</p>
                                    <div className="flex flex-wrap gap-2">
                                        {allColors.map(color => (
                                            <button key={color} onClick={() => setSelectedColor(selectedColor === color ? '' : color)}
                                                className="px-3 py-1 text-xs transition-colors"
                                                style={{
                                                    borderRadius: t.buttonRadius,
                                                    background: selectedColor === color ? t.colors.accent : 'transparent',
                                                    color: selectedColor === color ? t.colors.badgeText : t.colors.textMuted,
                                                    border: `1px solid ${selectedColor === color ? t.colors.accent : t.colors.border}`
                                                }}>
                                                {color}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-wider mb-2" style={{ color: t.colors.textMuted }}>Talla</p>
                                    <div className="flex flex-wrap gap-2">
                                        {allTallas.map(talla => (
                                            <button key={talla} onClick={() => setSelectedTalla(selectedTalla === talla ? '' : talla)}
                                                className="w-10 h-10 rounded-lg text-xs transition-colors"
                                                style={{
                                                    background: selectedTalla === talla ? t.colors.accent : 'transparent',
                                                    color: selectedTalla === talla ? t.colors.badgeText : t.colors.textMuted,
                                                    border: `1px solid ${selectedTalla === talla ? t.colors.accent : t.colors.border}`
                                                }}>
                                                {talla}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-wider mb-2" style={{ color: t.colors.textMuted }}>Categoria</p>
                                    <div className="flex flex-wrap gap-2">
                                        {allCategorias.map(cat => (
                                            <button key={cat} onClick={() => setSelectedCategoria(selectedCategoria === cat ? '' : cat)}
                                                className="px-3 py-1 text-xs transition-colors"
                                                style={{
                                                    borderRadius: t.buttonRadius,
                                                    background: selectedCategoria === cat ? t.colors.accent : 'transparent',
                                                    color: selectedCategoria === cat ? t.colors.badgeText : t.colors.textMuted,
                                                    border: `1px solid ${selectedCategoria === cat ? t.colors.accent : t.colors.border}`
                                                }}>
                                                {cat}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex items-end">
                                    <button onClick={clearFilters} className="text-xs hover:underline" style={{ color: t.colors.accent }}>Limpiar filtros</button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Product Grid */}
                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="w-10 h-10 border-2 rounded-full animate-spin" style={{ borderColor: `${t.colors.accent}33`, borderTopColor: t.colors.accent }} />
                    </div>
                ) : (activeCat === 'playeras') ? (
                    // Vista de playeras (con carrito + visor 360)
                    (() => {
                        if (playeras.length === 0) {
                            return (
                                <div className="text-center py-20">
                                    <ShoppingBag className="w-16 h-16 mx-auto mb-4" style={{ color: t.colors.border }} />
                                    <p className="text-lg" style={{ color: t.colors.textMuted }}>Sin disenos disponibles</p>
                                    <p className="text-sm mt-2" style={{ color: t.colors.textMuted, opacity: 0.6 }}>Agrega disenos desde el panel de Playeras en administracion</p>
                                </div>
                            );
                        }
                        return (
                            <div className={`grid gap-4 md:gap-6 ${gridClass}`}>
                                {playeras.map((pl) => (
                                    <PlayeraCard key={pl.id} playera={pl} theme={t} onAddToCart={addToCart} ventasConfig={config} isFavorito={favoritos.includes(pl.id)} onToggleFav={toggleFavorito} />
                                ))}
                            </div>
                        );
                    })()
                ) : filtered.length > 0 ? (
                    (() => {
                        // Agrupar productos por marca (manteniendo el orden de aparicion del primer producto de cada marca)
                        const ordenMarcas = [];
                        const grupos = {};
                        for (const p of filtered) {
                            const marca = (p.marca || 'Sin marca').trim() || 'Sin marca';
                            if (!grupos[marca]) {
                                grupos[marca] = [];
                                ordenMarcas.push(marca);
                            }
                            grupos[marca].push(p);
                        }
                        const hayUnaSolaMarca = ordenMarcas.length === 1;
                        return (
                            <div className="space-y-12">
                                {ordenMarcas.map((marca, gIdx) => {
                                    // Buscar info enriquecida de la marca (si admin la configuro)
                                    const marcaInfo = (marcasAdmin || []).find(m => (m.nombre || '').toLowerCase() === marca.toLowerCase());
                                    return (
                                    <div key={marca} data-testid={`grupo-marca-${marca}`}>
                                        {/* Encabezado de marca enriquecido (oculto si solo hay una marca) */}
                                        {!hayUnaSolaMarca && (
                                            <div
                                                className="relative overflow-hidden rounded-xl mb-6 p-4 sm:p-6"
                                                style={{
                                                    background: marcaInfo?.banner_url
                                                        ? `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.65)), url(${marcaInfo.banner_url})`
                                                        : `linear-gradient(135deg, ${t.colors.surface}, ${t.colors.bg})`,
                                                    backgroundSize: 'cover',
                                                    backgroundPosition: 'center',
                                                    border: `1px solid ${t.colors.border}`,
                                                }}
                                            >
                                                <div className="flex items-center gap-3 sm:gap-4">
                                                    {marcaInfo?.logo_url && (
                                                        <div className="shrink-0 w-14 h-14 sm:w-20 sm:h-20 rounded-lg overflow-hidden p-1" style={{ background: '#fff' }}>
                                                            <img src={marcaInfo.logo_url} alt={marca} className="w-full h-full object-contain" />
                                                        </div>
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="text-xl sm:text-3xl font-black tracking-tight uppercase" style={{ color: marcaInfo?.banner_url ? '#fff' : t.colors.text, fontFamily: t.font }}>
                                                            {marca}
                                                        </h3>
                                                        {marcaInfo?.descripcion && (
                                                            <p className="text-xs sm:text-sm mt-0.5" style={{ color: marcaInfo?.banner_url ? '#fff' : t.colors.textMuted, opacity: 0.85 }}>{marcaInfo.descripcion}</p>
                                                        )}
                                                        <p className="text-[10px] sm:text-xs mt-1 font-bold uppercase tracking-wider" style={{ color: t.colors.accent }}>
                                                            {grupos[marca].length} modelo{grupos[marca].length !== 1 ? 's' : ''} disponible{grupos[marca].length !== 1 ? 's' : ''}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div className={`grid gap-4 md:gap-6 ${gridClass}`}>
                                            {grupos[marca].map((producto, i) => (
                                                useAnimations ? (
                                                    <motion.div key={producto.id} custom={gIdx * 10 + i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                                                        <ProductCard producto={producto} onAddToCart={addToCart} theme={t} isFavorito={favoritos.includes(producto.id)} onToggleFav={toggleFavorito} />
                                                    </motion.div>
                                                ) : (
                                                    <div key={producto.id}>
                                                        <ProductCard producto={producto} onAddToCart={addToCart} theme={t} isFavorito={favoritos.includes(producto.id)} onToggleFav={toggleFavorito} />
                                                    </div>
                                                )
                                            ))}
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        );
                    })()
                ) : (
                    <div className="text-center py-20">
                        <ShoppingBag className="w-16 h-16 mx-auto mb-4" style={{ color: t.colors.border }} />
                        <p className="text-lg" style={{ color: t.colors.textMuted }}>No se encontraron productos en esta categoria</p>
                        {productos.length === 0 && <p className="text-sm mt-2" style={{ color: t.colors.textMuted, opacity: 0.6 }}>Agrega productos desde el panel de administracion</p>}
                    </div>
                )}
            </section>

            {/* Cart Drawer */}
            <AnimatePresence>
                {cartOpen && (
                    <>
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 z-50" onClick={() => setCartOpen(false)} />
                        <motion.div
                            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            className="fixed top-0 right-0 w-full max-w-md h-screen z-50 flex flex-col"
                            style={{ background: t.colors.bg, borderLeft: `1px solid ${t.colors.border}` }}
                            data-testid="cart-drawer">
                            <div className="flex items-center justify-between p-5" style={{ borderBottom: `1px solid ${t.colors.border}` }}>
                                <h3 className="text-xl tracking-wider font-bold" style={{ fontFamily: t.font }}>CARRITO</h3>
                                <button onClick={() => setCartOpen(false)} className="p-1 rounded-lg hover:opacity-70">
                                    <X className="w-5 h-5" style={{ color: t.colors.textMuted }} />
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-5 space-y-4">
                                {cart.length === 0 ? (
                                    <div className="text-center py-16">
                                        <ShoppingBag className="w-12 h-12 mx-auto mb-3" style={{ color: t.colors.border }} />
                                        <p style={{ color: t.colors.textMuted }}>Tu carrito esta vacio</p>
                                    </div>
                                ) : cart.map((item, index) => (
                                    <div key={item.id || index} className="flex gap-3 p-3" style={{ background: t.colors.surface, borderRadius: t.cardRadius }} data-testid={`cart-item-${index}`}>
                                        <div className="w-16 h-16 rounded-lg flex items-center justify-center shrink-0 overflow-hidden"
                                            style={{ background: t.colors.border }}>
                                            {item.imagen ? (
                                                <img src={item.imagen} alt={item.nombre} className="w-full h-full object-cover" />
                                            ) : item.imagenes?.[0] ? (
                                                <img src={item.imagenes[0]} alt={item.nombre} className="w-full h-full object-cover" />
                                            ) : (
                                                <ShoppingBag className="w-6 h-6" style={{ color: t.colors.textMuted }} />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium truncate">{item.nombre}</p>
                                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                                <span className="text-[10px] px-1.5 py-0.5 rounded font-bold capitalize"
                                                    style={{
                                                        background: (item.tipo_compra || 'pieza') === 'paquete' ? '#3B82F6' : (item.tipo_compra || 'pieza') === 'corrida' ? '#8B5CF6' : t.colors.accent,
                                                        color: '#fff'
                                                    }}>
                                                    {item.tipo_compra || 'pieza'}
                                                </span>
                                                <span className="text-[11px]" style={{ color: t.colors.textMuted }}>{item.talla}</span>
                                            </div>
                                            <div className="flex items-center gap-2 mt-1">
                                                <button onClick={() => updateCantidad(index, -1)}
                                                    className="w-6 h-6 rounded flex items-center justify-center"
                                                    style={{ background: t.colors.bg, border: `1px solid ${t.colors.border}` }}
                                                    data-testid={`cart-minus-${index}`}>
                                                    <Minus className="w-3 h-3" />
                                                </button>
                                                <span className="text-xs font-bold w-5 text-center">{item.cantidad}</span>
                                                <button onClick={() => updateCantidad(index, 1)}
                                                    className="w-6 h-6 rounded flex items-center justify-center"
                                                    style={{ background: t.colors.bg, border: `1px solid ${t.colors.border}` }}
                                                    data-testid={`cart-plus-${index}`}>
                                                    <Plus className="w-3 h-3" />
                                                </button>
                                                <span className="ml-auto text-sm font-bold" style={{ color: t.colors.accent }}>${(item.precio * item.cantidad).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                                            </div>
                                        </div>
                                        <button onClick={() => removeFromCart(index)} className="p-1 hover:opacity-70 self-start" style={{ color: t.colors.textMuted }} data-testid={`cart-remove-${index}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            {cart.length > 0 && (() => {
                                // Calcular contadores y ahorro estimado
                                const counts = { pieza: 0, paquete: 0, corrida: 0 };
                                let totalPiezas = 0;
                                let ahorro = 0;
                                cart.forEach(item => {
                                    const tc = item.tipo_compra || 'pieza';
                                    counts[tc] = (counts[tc] || 0) + 1;
                                    totalPiezas += (item.cantidad || 0);
                                    // Ahorro: si la compra es paquete/corrida vs pieza
                                    if (tc === 'paquete' || tc === 'corrida') {
                                        // Estimar precio individual: si el carrito tiene "precio_unitario_pieza", usarlo
                                        // Si no, asumir precio item es paquete/corrida y comparar precio del item / cantidad vs precio del item solo
                                        // (no tenemos precio pieza guardado, usamos heuristica: paquete vs pieza-equivalente)
                                        const precioUnit = (item.precio || 0) / (item.cantidad || 1);
                                        const precioListaUnit = item.precio_unitario_pieza || (precioUnit * 1.25); // ahorro estimado ~20-25%
                                        const ahorroItem = (precioListaUnit - precioUnit) * (item.cantidad || 0);
                                        if (ahorroItem > 0) ahorro += ahorroItem;
                                    }
                                });
                                return (
                                    <div className="p-4 space-y-3" style={{ borderTop: `1px solid ${t.colors.border}` }}>
                                        {/* Resumen visual */}
                                        <div className="grid grid-cols-3 gap-2 text-center" data-testid="cart-resumen-tipos">
                                            <div className="p-2 rounded-lg" style={{ background: t.colors.surface }}>
                                                <p className="text-xl font-bold" style={{ color: t.colors.accent, fontFamily: t.font }}>{counts.paquete}</p>
                                                <p className="text-[9px] uppercase tracking-wider" style={{ color: t.colors.textMuted }}>Paquetes</p>
                                            </div>
                                            <div className="p-2 rounded-lg" style={{ background: t.colors.surface }}>
                                                <p className="text-xl font-bold" style={{ color: t.colors.accent, fontFamily: t.font }}>{counts.corrida}</p>
                                                <p className="text-[9px] uppercase tracking-wider" style={{ color: t.colors.textMuted }}>Corridas</p>
                                            </div>
                                            <div className="p-2 rounded-lg" style={{ background: t.colors.surface }}>
                                                <p className="text-xl font-bold" style={{ color: t.colors.accent, fontFamily: t.font }}>{counts.pieza}</p>
                                                <p className="text-[9px] uppercase tracking-wider" style={{ color: t.colors.textMuted }}>Piezas</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between text-xs" style={{ color: t.colors.textMuted }}>
                                            <span>Total piezas: <b style={{ color: t.colors.text }}>{totalPiezas}</b></span>
                                            {ahorro > 0 && (
                                                <span className="px-2 py-0.5 rounded-full font-bold text-emerald-400" style={{ background: 'rgba(16,185,129,0.1)' }}>
                                                    Ahorras ~${Math.round(ahorro).toLocaleString('es-MX')}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex justify-between items-center pt-2 border-t" style={{ borderColor: t.colors.border }}>
                                            <span style={{ color: t.colors.textMuted }}>Total:</span>
                                            <span className="text-2xl font-bold" style={{ color: t.colors.accent, fontFamily: t.font }}>${cartTotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                                        </div>
                                        <button onClick={checkoutWhatsApp}
                                            className="w-full bg-green-500 hover:bg-green-400 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
                                            data-testid="checkout-whatsapp-btn">
                                            <MessageCircle className="w-5 h-5" />
                                            Ordenar por WhatsApp
                                        </button>
                                    </div>
                                );
                            })()}
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Footer */}
            <footer className="py-12 mt-16" style={{ borderTop: `1px solid ${t.colors.border}` }}>
                <div className="max-w-7xl mx-auto px-4">
                    {/* TikTok section */}
                    {Array.isArray(config?.tiktok_videos) && config.tiktok_videos.length > 0 && (
                        <div className="mb-12">
                            <div className="text-center mb-6">
                                <p className="text-xs tracking-[0.3em] uppercase mb-2" style={{ color: t.colors.accent }}>Asi luce puesto</p>
                                <h3 className="text-2xl sm:text-3xl font-bold">Clientes en TikTok</h3>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {config.tiktok_videos.slice(0, 6).map((url, i) => (
                                    <TikTokEmbed key={i} url={url} />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Location & contact */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                        <div>
                            <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: t.colors.accent }}>Visitanos</p>
                            <h3 className="text-xl font-bold mb-3">Pasa a nuestra tienda</h3>
                            {config?.tienda_direccion && (
                                <div className="flex items-start gap-2 mb-2 text-sm" style={{ color: t.colors.textMuted }}>
                                    <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                                    <span>{config.tienda_direccion}</span>
                                </div>
                            )}
                            {config?.tienda_horarios && (
                                <div className="flex items-start gap-2 mb-2 text-sm" style={{ color: t.colors.textMuted }}>
                                    <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                                    <span style={{ whiteSpace: 'pre-line' }}>{config.tienda_horarios}</span>
                                </div>
                            )}
                            {config?.whatsapp_numero && (
                                <div className="flex items-start gap-2 mb-4 text-sm" style={{ color: t.colors.textMuted }}>
                                    <Phone className="w-4 h-4 mt-0.5 shrink-0" />
                                    <span>+{config.whatsapp_numero}</span>
                                </div>
                            )}
                            {config?.google_maps_link && (
                                <a href={config.google_maps_link} target="_blank" rel="noreferrer"
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-opacity hover:opacity-80"
                                    style={{ background: t.colors.accent, color: t.colors.badgeText }}
                                    data-testid="btn-como-llegar">
                                    <MapPin className="w-4 h-4" /> Como llegar
                                    <ExternalLink className="w-3 h-3" />
                                </a>
                            )}
                        </div>
                        <div>
                            {config?.google_maps_embed && (
                                <div className="rounded-lg overflow-hidden border" style={{ borderColor: t.colors.border, aspectRatio: '16/10' }}>
                                    <iframe
                                        src={config.google_maps_embed}
                                        width="100%" height="100%"
                                        style={{ border: 0 }}
                                        allowFullScreen
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                        title="Ubicacion tienda"
                                    />
                                </div>
                            )}
                            {!config?.google_maps_embed && config?.google_maps_link && (
                                <div className="rounded-lg p-8 text-center" style={{ background: t.colors.surface, border: `1px solid ${t.colors.border}` }}>
                                    <MapPin className="w-12 h-12 mx-auto mb-3" style={{ color: t.colors.accent }} />
                                    <p className="text-sm mb-3" style={{ color: t.colors.textMuted }}>Abre la ubicacion en tu app de mapas</p>
                                    <a href={config.google_maps_link} target="_blank" rel="noreferrer" className="text-sm font-bold underline" style={{ color: t.colors.accent }}>
                                        Abrir en Google Maps
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="text-center mb-8">
                        <button data-testid="footer-offline-catalog-btn" onClick={() => setShowOffline(true)}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold"
                            style={{ background: t.colors.surface, border: `1px solid ${t.colors.border}`, color: t.colors.text }}>
                            <Download className="w-4 h-4" style={{ color: t.colors.accent }} /> Descargar catálogo offline
                        </button>
                    </div>

                    {/* Branding */}
                    <div className="text-center pt-6" style={{ borderTop: `1px solid ${t.colors.border}` }}>
                        <div className="flex items-center justify-center gap-2 mb-3">
                            <img src="/logo-karry.png" alt="Karry Jean's" className="w-8 h-8 rounded-md object-contain" />
                            <span className="text-lg tracking-wider font-bold" style={{ fontFamily: t.font }}>KARRY JEAN'S</span>
                        </div>
                        <p className="text-sm" style={{ color: t.colors.textMuted }}>Fabricantes directos &mdash; Los mejores precios de Mexico</p>
                        <p className="text-xs mt-3" style={{ color: t.colors.textMuted, opacity: 0.5 }}>&copy; {new Date().getFullYear()} Karry Jean's. Todos los derechos reservados.</p>
                        <p className="text-[10px] mt-1 font-mono" style={{ color: t.colors.textMuted, opacity: 0.4 }}>App version: kj-v53 &middot; <Link to="/limpiar-cache" className="underline hover:opacity-80">Limpiar cache</Link></p>

                        {/* Codigo de seguimiento - acceso */}
                        <div className="mt-4 flex items-center justify-center gap-2 flex-wrap text-xs">
                            <span style={{ color: t.colors.textMuted }}>¿Tienes un codigo de seguimiento?</span>
                            <button
                                onClick={() => {
                                    const code = prompt('Ingresa tu codigo de seguimiento (6 caracteres):');
                                    if (code && code.trim()) window.location.href = `/pedido/${code.trim().toUpperCase()}`;
                                }}
                                className="underline font-bold"
                                style={{ color: t.colors.accent }}
                                data-testid="btn-buscar-pedido"
                            >
                                Consultar mi pedido
                            </button>
                        </div>
                    </div>
                </div>
            </footer>
            <CatalogoOfflineModal open={showOffline} onClose={() => setShowOffline(false)} t={t} />

            {/* Floating WhatsApp action buttons - solo desktop */}
            <div className="hidden sm:flex fixed bottom-4 right-4 z-40 flex-col gap-2" data-testid="floating-wa-buttons">
                <button
                    onClick={() => sendQuickMessage('cotizar')}
                    className="px-3 py-2 rounded-full shadow-lg text-xs font-bold flex items-center gap-1.5 transition-transform hover:scale-105"
                    style={{ background: '#10B981', color: '#fff' }}
                    data-testid="btn-cotizar"
                    title="Solicitar cotizacion por WhatsApp"
                >
                    <MessageCircle className="w-3.5 h-3.5" /> Cotizar
                </button>
                <button
                    onClick={() => sendQuickMessage('contactar')}
                    className="px-3 py-2 rounded-full shadow-lg text-xs font-bold flex items-center gap-1.5 transition-transform hover:scale-105"
                    style={{ background: '#25D366', color: '#fff' }}
                    data-testid="btn-contactar"
                    title="Contactar por WhatsApp"
                >
                    <MessageCircle className="w-3.5 h-3.5" /> Contactar
                </button>
            </div>

            {/* BOTTOM NAVIGATION BAR (solo movil) - Panel de acciones rapidas */}
            <div
                className="sm:hidden fixed bottom-0 left-0 right-0 z-40 backdrop-blur-xl border-t flex items-stretch"
                style={{ background: `${t.colors.bg}f5`, borderColor: t.colors.border }}
                data-testid="bottom-nav-mobile"
            >
                {[
                    { id: 'buscar', label: 'Buscar', icon: Search, action: () => { document.querySelector('[data-testid=search-productos]')?.focus(); window.scrollTo({ top: 0, behavior: 'smooth' }); } },
                    { id: 'marcas', label: 'Marcas', icon: Tag, action: () => setShowMarcasMenu(true), badge: marcasInfo.length },
                    { id: 'favoritos', label: 'Favoritos', icon: Heart, action: () => setShowFavoritos(true), badge: favoritos.length || 0 },
                    { id: 'carrito', label: 'Carrito', icon: ShoppingBag, action: () => setCartOpen(true), badge: cart.reduce((s, i) => s + i.cantidad, 0) },
                    { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, action: () => sendQuickMessage('contactar') },
                ].map(b => (
                    <button
                        key={b.id}
                        onClick={b.action}
                        className="flex-1 flex flex-col items-center justify-center py-2.5 gap-0.5 relative active:opacity-60 transition"
                        style={{ color: t.colors.textMuted }}
                        data-testid={`bnav-${b.id}`}
                    >
                        <b.icon className="w-5 h-5" />
                        <span className="text-[9px] font-bold uppercase tracking-wider">{b.label}</span>
                        {b.badge > 0 && (
                            <span className="absolute top-1.5 right-[28%] min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center" style={{ background: t.colors.accent, color: t.colors.badgeText }}>
                                {b.badge > 99 ? '99+' : b.badge}
                            </span>
                        )}
                    </button>
                ))}
            </div>
            {/* Spacer para que el ultimo producto no quede tapado por bottom nav en movil */}
            <div className="h-16 sm:hidden" aria-hidden />

            {/* DRAWER de MARCAS (menu flotante) */}
            <AnimatePresence>
                {showMarcasMenu && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 z-[80]"
                            onClick={() => setShowMarcasMenu(false)}
                        />
                        <motion.div
                            initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
                            transition={{ type: 'spring', damping: 28, stiffness: 280 }}
                            className="fixed left-0 top-0 bottom-0 w-[85%] max-w-sm z-[81] overflow-y-auto"
                            style={{ background: t.colors.bg, borderRight: `1px solid ${t.colors.border}` }}
                            data-testid="marcas-drawer"
                        >
                            <div className="sticky top-0 px-5 py-4 flex items-center justify-between" style={{ background: t.colors.bg, borderBottom: `1px solid ${t.colors.border}` }}>
                                <h3 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: t.font }}>
                                    <Tag className="w-5 h-5" style={{ color: t.colors.accent }} /> MARCAS
                                </h3>
                                <button onClick={() => setShowMarcasMenu(false)} className="p-1 rounded-lg hover:opacity-70">
                                    <X className="w-5 h-5" style={{ color: t.colors.textMuted }} />
                                </button>
                            </div>
                            <div className="p-3 space-y-1">
                                <button
                                    onClick={() => { setSelectedMarca(''); setShowMarcasMenu(false); document.getElementById('catalogo')?.scrollIntoView({ behavior: 'smooth' }); }}
                                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition text-sm font-medium"
                                    style={{ background: !selectedMarca ? `${t.colors.accent}20` : 'transparent', color: !selectedMarca ? t.colors.accent : t.colors.text }}
                                >
                                    <span>Todas las marcas</span>
                                    <span className="text-[10px] opacity-60">{productos.length}</span>
                                </button>
                                {marcasInfo.length === 0 ? (
                                    <p className="text-xs text-center py-6" style={{ color: t.colors.textMuted }}>Aun no hay marcas registradas en los productos.</p>
                                ) : marcasInfo.map(m => (
                                    <button
                                        key={m.nombre}
                                        onClick={() => {
                                            setSelectedMarca(m.nombre);
                                            setShowMarcasMenu(false);
                                            setTimeout(() => {
                                                const el = document.querySelector(`[data-testid="grupo-marca-${m.nombre}"]`);
                                                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                else document.getElementById('catalogo')?.scrollIntoView({ behavior: 'smooth' });
                                            }, 100);
                                        }}
                                        className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition text-sm font-medium hover:opacity-80"
                                        style={{ background: selectedMarca === m.nombre ? `${t.colors.accent}20` : 'transparent', color: selectedMarca === m.nombre ? t.colors.accent : t.colors.text }}
                                        data-testid={`drawer-marca-${m.nombre}`}
                                    >
                                        <span className="capitalize">{m.nombre}</span>
                                        <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: t.colors.surface, color: t.colors.textMuted }}>{m.count}</span>
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* DRAWER de FAVORITOS */}
            <AnimatePresence>
                {showFavoritos && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 z-[80]"
                            onClick={() => setShowFavoritos(false)}
                        />
                        <motion.div
                            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 28, stiffness: 280 }}
                            className="fixed right-0 top-0 bottom-0 w-[90%] max-w-md z-[81] overflow-y-auto"
                            style={{ background: t.colors.bg, borderLeft: `1px solid ${t.colors.border}` }}
                            data-testid="favoritos-drawer"
                        >
                            <div className="sticky top-0 px-5 py-4 flex items-center justify-between" style={{ background: t.colors.bg, borderBottom: `1px solid ${t.colors.border}` }}>
                                <h3 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: t.font }}>
                                    <Heart className="w-5 h-5 fill-pink-500 text-pink-500" /> FAVORITOS ({favoritos.length})
                                </h3>
                                <button onClick={() => setShowFavoritos(false)} className="p-1 rounded-lg hover:opacity-70">
                                    <X className="w-5 h-5" style={{ color: t.colors.textMuted }} />
                                </button>
                            </div>
                            <div className="p-4">
                                {(() => {
                                    const favProductos = productos.filter(p => favoritos.includes(p.id));
                                    if (favProductos.length === 0) return (
                                        <div className="text-center py-12">
                                            <Heart className="w-12 h-12 mx-auto mb-3 opacity-30" style={{ color: t.colors.border }} />
                                            <p className="text-sm" style={{ color: t.colors.textMuted }}>Aun no tienes favoritos</p>
                                            <p className="text-[11px] mt-1" style={{ color: t.colors.textMuted, opacity: 0.6 }}>Toca el corazon en un producto para guardarlo</p>
                                        </div>
                                    );
                                    return (
                                        <div className="grid grid-cols-2 gap-3">
                                            {favProductos.map(p => (
                                                <div key={p.id}>
                                                    <ProductCard producto={p} theme={t} isFavorito={true} onToggleFav={toggleFavorito} />
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })()}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* CHECKOUT MODAL */}
            <AnimatePresence>
                {checkoutOpen && (
                    <>
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/70 z-[70]" onClick={() => !submitting && setCheckoutOpen(false)} />
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                            className="fixed inset-0 z-[71] flex items-center justify-center p-4">
                            <div className="w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden"
                                style={{ background: t.colors.bg, border: `1px solid ${t.colors.border}`, color: t.colors.text }}
                                data-testid="checkout-modal">
                                <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: t.colors.border }}>
                                    <h3 className="text-lg font-bold">Datos para tu pedido</h3>
                                    <button onClick={() => !submitting && setCheckoutOpen(false)} className="p-1" disabled={submitting}>
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>
                                <div className="p-5 max-h-[70vh] overflow-y-auto space-y-3">
                                    <div className="text-xs px-3 py-2 rounded" style={{ background: t.colors.surface, color: t.colors.textMuted }}>
                                        Total del pedido: <b style={{ color: t.colors.accent }}>${cartTotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</b> &middot; {cart.length} producto{cart.length !== 1 ? 's' : ''}
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold uppercase tracking-wide block mb-1">Tu nombre *</label>
                                        <input type="text" value={checkoutData.nombre}
                                            onChange={(e) => setCheckoutData({ ...checkoutData, nombre: e.target.value })}
                                            placeholder="Nombre completo"
                                            className="w-full px-3 py-2 rounded-lg text-sm"
                                            style={{ background: t.colors.surface, border: `1px solid ${t.colors.border}`, color: t.colors.text }}
                                            data-testid="checkout-nombre" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold uppercase tracking-wide block mb-1">Telefono / WhatsApp *</label>
                                        <PhoneInput
                                            value={checkoutData.telefono}
                                            onChange={(v) => setCheckoutData({ ...checkoutData, telefono: v })}
                                            placeholder="5512345678"
                                            testId="checkout-tel"
                                        />
                                        <p className="text-[10px] mt-1" style={{ color: t.colors.textMuted }}>Default Mexico (+52). Cambia la bandera para otro pais.</p>
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold uppercase tracking-wide block mb-2">Forma de entrega *</label>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                            {[
                                                { v: 'tienda', l: 'Recoger en tienda', sub: 'Pasa tu mismo por el pedido', icon: '🏪' },
                                                { v: 'paqueteria', l: 'Paqueteria', sub: 'DHL, Estafeta, FedEx, etc.', icon: '📦' },
                                                { v: 'camion', l: 'Camion Texticuitzeo', sub: 'Linea propia de Texticuitzeo', icon: '🚛' }
                                            ].map(o => (
                                                <button key={o.v} type="button"
                                                    onClick={() => setCheckoutData({ ...checkoutData, metodo_envio: o.v })}
                                                    className="px-3 py-3 rounded-lg text-xs font-bold transition-all text-left"
                                                    style={{
                                                        background: checkoutData.metodo_envio === o.v ? t.colors.accent : t.colors.surface,
                                                        color: checkoutData.metodo_envio === o.v ? t.colors.badgeText : t.colors.text,
                                                        border: `2px solid ${checkoutData.metodo_envio === o.v ? t.colors.accent : t.colors.border}`,
                                                        transform: checkoutData.metodo_envio === o.v ? 'scale(1.02)' : 'scale(1)'
                                                    }}
                                                    data-testid={`envio-${o.v}`}>
                                                    <div className="text-lg mb-0.5">{o.icon}</div>
                                                    <div className="font-bold text-xs">{o.l}</div>
                                                    <div className="text-[10px] mt-0.5 opacity-80 font-normal">{o.sub}</div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {checkoutData.metodo_envio === 'paqueteria' && (
                                        <>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="text-xs font-bold uppercase tracking-wide block mb-1">Ciudad</label>
                                                    <input type="text" value={checkoutData.ciudad}
                                                        onChange={(e) => setCheckoutData({ ...checkoutData, ciudad: e.target.value })}
                                                        className="w-full px-3 py-2 rounded-lg text-sm"
                                                        style={{ background: t.colors.surface, border: `1px solid ${t.colors.border}`, color: t.colors.text }} />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold uppercase tracking-wide block mb-1">Estado</label>
                                                    <input type="text" value={checkoutData.estado}
                                                        onChange={(e) => setCheckoutData({ ...checkoutData, estado: e.target.value })}
                                                        className="w-full px-3 py-2 rounded-lg text-sm"
                                                        style={{ background: t.colors.surface, border: `1px solid ${t.colors.border}`, color: t.colors.text }} />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold uppercase tracking-wide block mb-1">Direccion completa</label>
                                                <textarea value={checkoutData.direccion}
                                                    onChange={(e) => setCheckoutData({ ...checkoutData, direccion: e.target.value })}
                                                    placeholder="Calle, numero, colonia, CP"
                                                    rows={2}
                                                    className="w-full px-3 py-2 rounded-lg text-sm"
                                                    style={{ background: t.colors.surface, border: `1px solid ${t.colors.border}`, color: t.colors.text }} />
                                            </div>
                                        </>
                                    )}

                                    {checkoutData.metodo_envio === 'camion' && (
                                        <div>
                                            <label className="text-xs font-bold uppercase tracking-wide block mb-1">¿En que parte se encuentra el camion y quien recibe?</label>
                                            <textarea value={checkoutData.direccion}
                                                onChange={(e) => setCheckoutData({ ...checkoutData, direccion: e.target.value })}
                                                rows={2}
                                                className="w-full px-3 py-2 rounded-lg text-sm"
                                                style={{ background: t.colors.surface, border: `1px solid ${t.colors.border}`, color: t.colors.text }} />
                                        </div>
                                    )}

                                    {checkoutData.metodo_envio === 'tienda' && config?.tienda_direccion && (
                                        <div className="text-xs p-3 rounded" style={{ background: t.colors.surface, color: t.colors.textMuted }}>
                                            <p className="font-bold mb-1" style={{ color: t.colors.text }}>📍 Pasaras a:</p>
                                            <p>{config.tienda_direccion}</p>
                                            {config.tienda_horarios && <p className="mt-1">🕒 {config.tienda_horarios}</p>}
                                        </div>
                                    )}

                                    <div>
                                        <label className="text-xs font-bold uppercase tracking-wide block mb-1">Notas adicionales (opcional)</label>
                                        <textarea value={checkoutData.notas}
                                            onChange={(e) => setCheckoutData({ ...checkoutData, notas: e.target.value })}
                                            rows={2}
                                            className="w-full px-3 py-2 rounded-lg text-sm"
                                            style={{ background: t.colors.surface, border: `1px solid ${t.colors.border}`, color: t.colors.text }} />
                                    </div>
                                </div>
                                <div className="p-4 border-t flex gap-2" style={{ borderColor: t.colors.border }}>
                                    <Button variant="outline" onClick={() => setCheckoutOpen(false)} disabled={submitting} className="flex-1">Cancelar</Button>
                                    <button
                                        onClick={submitCheckout}
                                        disabled={submitting}
                                        className="flex-1 px-4 py-2 rounded-lg font-bold text-sm transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                                        style={{ background: '#25D366', color: '#fff' }}
                                        data-testid="btn-enviar-pedido"
                                    >
                                        {submitting ? 'Enviando...' : (<><MessageCircle className="w-4 h-4" /> Enviar por WhatsApp</>)}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </Wrap>
    );
};

// Card de playera para la vista publica. Sin precio - cotizacion por WhatsApp.
// Soporta vista 360 si tiene foto360 con multiples imagenes.
const PlayeraCard = ({ playera, theme: t, onAddToCart, ventasConfig, isFavorito, onToggleFav }) => {
    const fotos = playera.foto360 && playera.foto360.length > 0 ? playera.foto360 : (playera.foto ? [playera.foto] : []);
    const [idx, setIdx] = useState(0);
    const [view360, setView360] = useState(false);
    const [selectedTalla, setSelectedTalla] = useState('');
    const tallas = playera.tallas_disponibles || [];

    // Precios
    const precio = playera.precio || 0;
    const precioPaquete = playera.precio_paquete || 0;
    const tamanoPaquete = playera.tamano_paquete || 12;
    const corridaPzs = Math.ceil(tamanoPaquete / 2);
    const precioCorrida = playera.precio_corrida > 0
        ? playera.precio_corrida
        : (precioPaquete > 0 ? Math.round(((precioPaquete / tamanoPaquete) + 10) * corridaPzs * 100) / 100 : 0);

    // Tipos visibles segun flags globales del admin
    const piezaActiva = ventasConfig?.venta_pieza_activa !== false;
    const paqueteActiva = ventasConfig?.venta_paquete_activa !== false;
    const corridaActiva = ventasConfig?.venta_corrida_activa !== false;
    const showPieza = precio > 0 && piezaActiva;
    const showPaquete = precioPaquete > 0 && paqueteActiva;
    const showCorrida = precioCorrida > 0 && precioPaquete > 0 && corridaActiva;

    // Default: primer tipo disponible
    const [tipoCompra, setTipoCompra] = useState('pieza');
    useEffect(() => {
        if (showPieza) setTipoCompra('pieza');
        else if (showPaquete) setTipoCompra('paquete');
        else if (showCorrida) setTipoCompra('corrida');
    }, [showPieza, showPaquete, showCorrida]);

    const tieneAlgunPrecio = showPieza || showPaquete || showCorrida;

    const handleAdd = () => {
        if (tallas.length > 0 && !selectedTalla) { toast.error('Elige una talla'); return; }
        const precioUsado = tipoCompra === 'paquete' ? precioPaquete : tipoCompra === 'corrida' ? precioCorrida : precio;
        const producto = {
            id: playera.id,
            nombre: playera.estampado || 'Playera lisa',
            imagenes: fotos.length > 0 ? fotos : [],
            color: playera.color || '',
            precio: precioUsado,
            precio_paquete: precioPaquete,
            precio_corrida: precioCorrida,
            tamano_paquete: tamanoPaquete,
            es_playera: true,
        };
        // Reutilizar el flujo de carrito (mismo formato que ProductoDetalle)
        onAddToCart(producto, selectedTalla || (tallas[0] || ''), tipoCompra);
    };

    return (
        <div className="group" data-testid={`playera-card-${playera.id}`}>
            <div className="relative aspect-[3/4] overflow-hidden mb-3"
                style={{ background: t.colors.surface, borderRadius: t.cardRadius, border: `1px solid ${t.colors.border}` }}>
                {fotos[idx] ? (
                    <img src={fotos[idx]} alt={playera.estampado || 'Playera'} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag className="w-12 h-12" style={{ color: t.colors.border }} />
                    </div>
                )}
                {playera.tiene_estampado && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded" style={{ background: '#EC4899', color: '#fff' }}>
                        Estampado
                    </div>
                )}
                {fotos.length > 1 && (
                    <button onClick={() => setView360(true)} className="absolute top-2 right-2 px-2 py-1 rounded-full text-[10px] font-bold flex items-center gap-1"
                        style={{ background: 'rgba(0,0,0,0.7)', color: '#fff' }} data-testid={`playera-360-${playera.id}`}>
                        <RotateCw className="w-3 h-3" /> 360°
                    </button>
                )}
                {/* Boton favorito */}
                {onToggleFav && (
                    <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFav(playera.id); }}
                        className={`absolute ${fotos.length > 1 ? 'top-11' : 'top-2'} right-2 w-8 h-8 rounded-full flex items-center justify-center transition-all`}
                        style={{ background: isFavorito ? '#EC4899' : 'rgba(0,0,0,0.5)' }}
                        title={isFavorito ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                        data-testid={`btn-fav-${playera.id}`}
                    >
                        <Heart className={`w-4 h-4 ${isFavorito ? 'fill-white text-white' : 'text-white'}`} />
                    </button>
                )}
            </div>
            <div>
                <p className="text-sm font-medium truncate">{playera.estampado || 'Playera lisa'}</p>
                <p className="text-xs mt-0.5 capitalize" style={{ color: t.colors.textMuted }}>Color: {playera.color}</p>
                {tieneAlgunPrecio ? (
                    <p className="text-base mt-1 font-bold" style={{ color: t.colors.accent }}>
                        ${(tipoCompra === 'paquete' ? precioPaquete : tipoCompra === 'corrida' ? precioCorrida : precio).toLocaleString('es-MX')}
                    </p>
                ) : (
                    <p className="text-[10px] mt-1" style={{ color: t.colors.textMuted, fontStyle: 'italic' }}>Precio: Consultar al confirmar pedido</p>
                )}

                {/* Tipo de compra */}
                {tieneAlgunPrecio && (showPieza || showPaquete || showCorrida) && (
                    <div className="mt-2 flex gap-1">
                        {showPieza && (
                            <button onClick={() => setTipoCompra('pieza')}
                                className="flex-1 px-2 py-1 text-[10px] font-bold rounded transition"
                                style={{ background: tipoCompra === 'pieza' ? t.colors.accent : 'transparent', color: tipoCompra === 'pieza' ? t.colors.badgeText : t.colors.textMuted, border: `1px solid ${tipoCompra === 'pieza' ? t.colors.accent : t.colors.border}` }}
                                data-testid={`playera-tipo-pieza-${playera.id}`}>
                                Pieza
                            </button>
                        )}
                        {showPaquete && (
                            <button onClick={() => setTipoCompra('paquete')}
                                className="flex-1 px-2 py-1 text-[10px] font-bold rounded transition"
                                style={{ background: tipoCompra === 'paquete' ? t.colors.accent : 'transparent', color: tipoCompra === 'paquete' ? t.colors.badgeText : t.colors.textMuted, border: `1px solid ${tipoCompra === 'paquete' ? t.colors.accent : t.colors.border}` }}
                                data-testid={`playera-tipo-paquete-${playera.id}`}>
                                Paquete
                            </button>
                        )}
                        {showCorrida && (
                            <button onClick={() => setTipoCompra('corrida')}
                                className="flex-1 px-2 py-1 text-[10px] font-bold rounded transition"
                                style={{ background: tipoCompra === 'corrida' ? t.colors.accent : 'transparent', color: tipoCompra === 'corrida' ? t.colors.badgeText : t.colors.textMuted, border: `1px solid ${tipoCompra === 'corrida' ? t.colors.accent : t.colors.border}` }}
                                data-testid={`playera-tipo-corrida-${playera.id}`}>
                                Corrida
                            </button>
                        )}
                    </div>
                )}

                {/* Selector de talla */}
                {tallas.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                        {tallas.map(tl => (
                            <button
                                key={tl}
                                onClick={(e) => { e.stopPropagation(); setSelectedTalla(tl); }}
                                className="px-2 py-1 text-[10px] font-bold rounded transition"
                                style={{
                                    background: selectedTalla === tl ? t.colors.accent : 'transparent',
                                    color: selectedTalla === tl ? t.colors.badgeText : t.colors.textMuted,
                                    border: `1px solid ${selectedTalla === tl ? t.colors.accent : t.colors.border}`
                                }}
                                data-testid={`playera-talla-${playera.id}-${tl}`}
                            >
                                {tl}
                            </button>
                        ))}
                    </div>
                )}

                <button onClick={handleAdd}
                    className="mt-2 w-full px-3 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-opacity hover:opacity-90"
                    style={{ background: t.colors.accent, color: t.colors.badgeText }}
                    data-testid={`btn-playera-add-${playera.id}`}>
                    <ShoppingBag className="w-3.5 h-3.5" /> Agregar al carrito
                </button>
            </div>

            {/* Modal 360 */}
            {view360 && (
                <div className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4" data-testid="modal-360" onClick={() => setView360(false)}>
                    <div className="relative max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
                        <button className="absolute top-2 right-2 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center z-10" onClick={() => setView360(false)}>
                            <X className="w-5 h-5" />
                        </button>
                        <img src={fotos[idx]} alt="" className="w-full rounded-lg object-contain max-h-[80vh]" />
                        <div className="flex items-center justify-between mt-3 gap-2">
                            <button onClick={() => setIdx((idx - 1 + fotos.length) % fotos.length)} className="px-4 py-2 rounded bg-white/10 text-white text-sm font-bold flex items-center gap-1">← Anterior</button>
                            <span className="text-white text-sm font-mono">{idx + 1} / {fotos.length}</span>
                            <button onClick={() => setIdx((idx + 1) % fotos.length)} className="px-4 py-2 rounded bg-white/10 text-white text-sm font-bold flex items-center gap-1">Siguiente →</button>
                        </div>
                        {/* Thumbs */}
                        <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                            {fotos.map((f, i) => (
                                <button key={i} onClick={() => setIdx(i)} className="shrink-0 w-12 h-12 rounded overflow-hidden border-2" style={{ borderColor: i === idx ? '#fff' : 'transparent' }}>
                                    <img src={f} alt="" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Embed TikTok via blockquote oficial. Soporta URLs largas (con /video/ID) y cortas (vt.tiktok.com/XXX).
const TikTokEmbed = ({ url }) => {
    const [longUrl, setLongUrl] = useState(url);
    const [videoId, setVideoId] = useState(null);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        if (!url) return;
        setLoadError(false);
        // Si ya es URL larga con /video/ID
        const m = url.match(/\/video\/(\d+)/);
        if (m) {
            setLongUrl(url);
            setVideoId(m[1]);
            return;
        }
        // URL corta: resolver via oEmbed
        (async () => {
            try {
                const res = await fetch(`https://www.tiktok.com/oembed?url=${encodeURIComponent(url)}`);
                if (!res.ok) throw new Error('oembed fail');
                const data = await res.json();
                // data.html contiene blockquote con data-video-id
                const idMatch = (data.html || '').match(/data-video-id="(\d+)"/);
                const realUrl = (data.html || '').match(/cite="([^"]+)"/);
                if (idMatch) setVideoId(idMatch[1]);
                if (realUrl) setLongUrl(realUrl[1]);
                if (!idMatch) setLoadError(true);
            } catch (e) {
                setLoadError(true);
            }
        })();
    }, [url]);

    useEffect(() => {
        if (!videoId) return;
        // Cargar script una vez
        if (!document.querySelector('script[src*="tiktok.com/embed.js"]')) {
            const s = document.createElement('script');
            s.src = 'https://www.tiktok.com/embed.js';
            s.async = true;
            document.body.appendChild(s);
        } else if (window.tiktok && window.tiktok.reload) {
            try { window.tiktok.reload(); } catch { /* ignore */ }
        }
    }, [videoId]);

    if (loadError) {
        return (
            <a href={url} target="_blank" rel="noreferrer"
                className="aspect-[9/16] flex items-center justify-center text-xs underline mx-auto"
                style={{ maxWidth: '325px', minWidth: '280px', background: '#000', color: '#fff', borderRadius: 8 }}>
                Ver en TikTok ↗
            </a>
        );
    }

    if (!videoId) {
        // Cargando o sin resolver aun
        return <div className="aspect-[9/16] bg-zinc-200 rounded-lg flex items-center justify-center text-xs text-zinc-500 mx-auto animate-pulse" style={{ maxWidth: '325px', minWidth: '280px' }}>Cargando TikTok...</div>;
    }

    return (
        <blockquote
            className="tiktok-embed mx-auto"
            cite={longUrl}
            data-video-id={videoId}
            style={{ maxWidth: '325px', minWidth: '280px' }}
        >
            <section><a target="_blank" rel="noreferrer" href={longUrl}>Ver video</a></section>
        </blockquote>
    );
};

const ProductCard = ({ producto, theme: t, isFavorito, onToggleFav }) => {
    const etiquetas = producto.etiquetas || [];
    const tieneCorrida = (producto.precio_corrida || 0) > 0;
    const tienePaquete = (producto.precio_paquete || 0) > 0;
    // SKU autogenerado si no existe (no se persiste, solo display)
    const sku = producto.sku || (`${(producto.marca || 'KJ').slice(0, 3).toUpperCase()}-${(producto.id || '').slice(-6).toUpperCase()}`);
    return (
        <Link to={`/producto/${producto.id}`} className="group block" data-testid={`product-card-${producto.id}`}>
            <div className="relative aspect-[3/4] overflow-hidden mb-3"
                style={{ background: t.colors.surface, borderRadius: t.cardRadius, border: `1px solid ${t.colors.border}` }}>
                {producto.imagenes?.[0] ? (
                    <img src={producto.imagenes[0]} alt={producto.nombre} loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag className="w-12 h-12" style={{ color: t.colors.border }} />
                    </div>
                )}
                {/* Etiquetas */}
                <div className="absolute top-2 left-2 flex flex-col gap-1">
                    {etiquetas.includes('nuevo') && (
                        <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded" style={{ background: '#10B981', color: '#fff' }}>Nuevo</span>
                    )}
                    {etiquetas.includes('mas_vendido') && (
                        <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded" style={{ background: '#F59E0B', color: '#000' }}>Mas vendido</span>
                    )}
                    {etiquetas.includes('oferta') && (
                        <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded" style={{ background: '#EF4444', color: '#fff' }}>Oferta</span>
                    )}
                    {(etiquetas.includes('mayoreo') || tienePaquete) && (
                        <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded" style={{ background: '#3B82F6', color: '#fff' }}>Mayoreo</span>
                    )}
                    {producto.destacado && !etiquetas.length && (
                        <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded"
                            style={{ background: t.colors.badge, color: t.colors.badgeText }}>Destacado</span>
                    )}
                </div>
                {/* Boton favorito */}
                {onToggleFav && (
                    <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFav(producto.id); }}
                        className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-all"
                        style={{ background: isFavorito ? '#EC4899' : 'rgba(0,0,0,0.5)' }}
                        title={isFavorito ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                        data-testid={`btn-fav-${producto.id}`}
                    >
                        <Heart className={`w-4 h-4 ${isFavorito ? 'fill-white text-white' : 'text-white'}`} />
                    </button>
                )}
                {/* CTA hover */}
                <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="bg-white text-zinc-900 text-center py-1.5 rounded text-xs font-bold">
                        Ver producto
                    </div>
                </div>
            </div>
            <div>
                <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-bold uppercase tracking-wider" style={{ color: t.colors.textMuted }}>{producto.marca || 'Sin marca'}</p>
                    <p className="text-[9px] font-mono" style={{ color: t.colors.textMuted, opacity: 0.6 }}>{sku}</p>
                </div>
                <p className="text-sm font-medium mt-0.5 line-clamp-2">{producto.nombre}</p>
                <div className="flex items-baseline gap-2 mt-1 flex-wrap">
                    <p className="text-base font-bold" style={{ color: t.colors.accent, fontFamily: t.font }}>${producto.precio?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                    <span className="text-[10px]" style={{ color: t.colors.textMuted }}>por pieza</span>
                </div>
                {(tienePaquete || tieneCorrida) && (
                    <div className="flex gap-1 mt-1 flex-wrap">
                        {tienePaquete && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: t.colors.surface, color: t.colors.textMuted }}>
                                Paquete ${producto.precio_paquete?.toLocaleString('es-MX')}
                            </span>
                        )}
                        {tieneCorrida && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: t.colors.surface, color: t.colors.textMuted }}>
                                Corrida ${producto.precio_corrida?.toLocaleString('es-MX')}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </Link>
    );
};
